// Configuration for Hex Map GM Tool

export const CONFIG = {
  // URL paths to tile assets (served from /public/content/ by Vite)
  TILE_PATH: '/content/tiles',
  FLAG_PATH: '/content/flags',

  // Tile image dimensions (pixels) - Hexton Hills tiles are 270x234
  TILE_SIZE: 270,

  // Hex circumradius for positioning calculations
  // For flat-top hexes: hexSize = tileWidth / 2 = 135
  HEX_SIZE: 135,

  // Hexton Hills rotation interpretation:
  // Tile rotation values in the JSON have 30° as the "base" for flat-top display.
  // To get the effective rotation while keeping tiles flat-top:
  //   effectiveRotation = rot - HEXTON_ROTATION_BASE
  // This allows tiles to be rotated in 60° increments to align features.
  HEXTON_ROTATION_BASE: 30,

  // Fog of War opacity values
  FOG_UNREVEALED_OPACITY: 0.4,  // Unrevealed tiles (dimmed)
  FOG_REVEALED_OPACITY: 1.0,     // Revealed tiles (full opacity)

  // Z-Index stacking hierarchy (prevents conflicts, maintains clear layering)
  Z_INDEX_BASE: 10,       // Base layer elements (hex grid hover effects)
  Z_INDEX_SIDEBAR: 100,   // Sidebar popups and tooltips
  Z_INDEX_MODAL: 1000,    // Modal dialogs (note editor, icon picker, print, etc.)
  Z_INDEX_OVERLAY: 9999,  // Critical overlays (player view elements)
  Z_INDEX_TOAST: 10000,   // Top-most layer (loading indicators, notifications)

  // Content limits
  NOTE_MAX_LENGTH: 10000, // Maximum characters in a note

  // Timing constants (milliseconds)
  BROADCAST_DELAY: 100,       // Delay before broadcasting to newly opened Player View
  TOAST_DURATION: 3000,        // Toast notification display time
  TOAST_FADE_DURATION: 300,    // Toast fade-out transition time
  PRINT_DELAY: 100,            // Delay before opening print dialog
  FOCUS_DELAY: 100,            // Delay before focusing input field

  // Zoom controls
  ZOOM: {
    IN_FACTOR: 1.2,           // Zoom in multiplier
    OUT_FACTOR: 0.8,          // Zoom out multiplier (inverse of 1.25)
  },

  // Visibility options (for notes, icons, and tokens)
  VISIBILITY_SHOW: 'show',   // Visible to players
  VISIBILITY_SECRET: 'secret', // GM only

  // BroadcastChannel name for GM-Player sync
  BROADCAST_CHANNEL_NAME: 'hex-map-gm',

  // Color Palette (centralized theme colors)
  COLORS: {
    // UI feedback colors (toast notifications, buttons, etc.)
    PRIMARY: '#3b82f6',                 // Blue for info/primary actions
    SUCCESS: '#10b981',                 // Green for success states
    SUCCESS_TINT: 'rgba(16, 185, 129, 0.1)', // Success color at 10% opacity for backgrounds
    ERROR: '#ef4444',                   // Red for errors/warnings
    WARNING: '#ff9800',                 // Orange for warnings/historical mode
    WARNING_BORDER: '#d97706',          // Amber-700 for warning borders
    WARNING_SHADOW: 'rgba(245, 158, 11, 0.3)', // Amber-400 shadow for warnings

    // Selection and interaction
    SELECTION_STROKE: '#4a9eff',        // Blue border for selected hex
    SELECTION_HOVER: '#66b3ff',         // Lighter blue for hover
    HOVER_STROKE: 'rgba(255, 255, 255, 0.8)', // White semi-transparent hover

    // Highlights and indicators
    HIGHLIGHT_YELLOW: '#fbbf24',        // Yellow/amber highlight
    HIGHLIGHT_BRIGHT_YELLOW: '#ffeb3b', // Bright yellow pulse

    // Token colors
    TOKEN_DEFAULT: '#9b59d6',           // Default purple token
    TOKEN_STROKE: '#fff',               // White token border
    TOKEN_TEXT: '#fff',                 // White token text

    // Visibility indicators
    VISIBILITY_SECRET_COLOR: '#ef4444', // Red for GM-only (secret)
    VISIBILITY_SHOW_COLOR: '#10b981',   // Green for player-visible (show)

    // Overlay backgrounds
    OVERLAY_DARK: 'rgba(0, 0, 0, 0.65)', // Semi-transparent black overlay
    OVERLAY_DARKER: 'rgba(0, 0, 0, 0.7)', // Darker overlay for emphasis

    // Display ID badge
    DISPLAY_ID_BG: '#2a2a3a',           // Dark background for display ID
    DISPLAY_ID_TEXT: '#ffffff',         // White text for display ID

    // Print mode grid
    GRID_LINE: '#666666',               // Gray grid lines

    // Text colors
    TEXT_MUTED: '#666666',              // Muted text for empty states and hints
    TEXT_SECONDARY: '#9ca3af',          // Gray-400 for secondary text
    TEXT_BLACK: '#000000',              // Black text for high contrast on light backgrounds
  },

  // Rendering ratios (multipliers for hex-map sizing)
  RENDERING: {
    TOKEN_RADIUS_RATIO: 0.3,        // Token radius as fraction of hex size
    STACK_BADGE_RADIUS_RATIO: 0.35, // Stack badge radius as fraction of hex size
    DISPLAY_ID_VERTICAL_OFFSET: 0.6, // Display ID vertical offset as fraction of font size
  },

  // Color Palette Arrays for UI components
  TOKEN_COLOR_PRESETS: [
    '#9b59d6', // Purple
    '#3b82f6', // Blue
    '#10b981', // Green
    '#ef4444', // Red
    '#f59e0b', // Amber
    '#eab308', // Yellow
    '#ec4899', // Pink
    '#14b8a6', // Teal
  ],

  ICON_COLOR_PRESETS: [
    '#ef4444', // Red
    '#f59e0b', // Amber
    '#10b981', // Green
    '#3b82f6', // Blue
    '#8b5cf6', // Violet
    '#ec4899', // Pink
    '#6b7280', // Gray
    '#000000', // Black
  ],
};

// URL builders for local tile assets
export const buildTileUrl = (tileId) =>
  `${CONFIG.TILE_PATH}/${tileId}.png`;

export const buildFlagUrl = (flagId) =>
  `${CONFIG.FLAG_PATH}/${flagId}_BaseColor.png`;

export const buildManifestUrl = () =>
  `${CONFIG.TILE_PATH}/manifest.json`;

/**
 * Tile-specific metadata for special rendering behaviors
 *
 * Tiles may have content that intentionally extends beyond the standard
 * hex boundary (234px height). This metadata helps the renderer handle
 * such tiles appropriately.
 */
export const TILE_METADATA = {
  'AL07': {
    // Castle tile with graphic bleeding beyond hex boundary
    hasBottomBleed: true,
    bleedHeight: 48,        // pixels beyond standard 234px (total: 282px)
    standardHeight: 234,    // actual hex boundary height
    description: 'Castle with intentional graphic bleed on bottom edge'
  }
};

/**
 * Get metadata for a tile ID
 * @param {string} tileId - Tile identifier (e.g., "AL07")
 * @returns {Object} Metadata object or empty object if no metadata exists
 */
export function getTileMetadata(tileId) {
  return TILE_METADATA[tileId] || {};
}
